import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import ApiURL from "../Api/Api";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { useNavigate } from "react-router-dom";
import { SidebarContext } from "./SidebarContext";
import TransactionProofImage from "./TransactionProofImage";

const ReturnRequestReport = () => {
  const [returnRequests, setReturnRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [searchStatus, setSearchStatus] = useState("");
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);
  const user_id = localStorage.getItem("id");
  const role = localStorage.getItem("role");
  const navigate = useNavigate();
  const requesterLabel = role === "company_owner" ? "Admin" : "Worker";
  const reportTitle = role === "company_owner" ? "ADMIN RETURN REQUEST REPORT" : "RETURN REQUEST REPORT";

  const fetchReturnRequests = async () => {
    if (!["admin", "worker", "company_owner"].includes(role) || !user_id) {
      setError("Access denied");
      toast.error("Access denied");
      return;
    }

    setIsLoading(true);
    try {
      const url = new URL(`${ApiURL}/getReturnRequests.php`);
      url.searchParams.append("user_id", user_id);
      url.searchParams.append("role", role);
      console.log('Fetching URL:', url.toString());

      const response = await fetch(url.toString());
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      const data = await response.json();

      console.log('Fetched return requests:', JSON.stringify(data, null, 2));

      if (data.status === "success") {
        const sortedRequests = (data.requests || []).map(req => ({
          ...req,
          total_return_amount: parseFloat(req.total_return_amount) || 0,
          transaction_count: parseInt(req.transaction_count) || 0,
          transactions: (req.transactions || []).map(txn => ({
            ...txn,
            return_amount: parseFloat(txn.return_amount) || 0,
            transaction_no: parseInt(txn.transaction_no) || 0
          }))
        })).sort((a, b) => a.id - b.id); // Sort by id in ascending order
        setReturnRequests(sortedRequests);
        setFilteredRequests(sortedRequests);
        setError(null);
      } else {
        throw new Error(data.message || "Failed to load return requests");
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError("Failed to fetch return requests: " + err.message);
      toast.error("Failed to fetch return requests: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReturnRequests();
  }, []);

  useEffect(() => {
    const filtered = returnRequests.filter((req) => {
      const matchesStatus = searchStatus ? req.status.toLowerCase() === searchStatus.toLowerCase() : true;
      const reqDate = new Date(req.created_at);
      const startDate = searchStartDate ? new Date(searchStartDate) : null;
      const endDate = searchEndDate ? new Date(searchEndDate) : null;
      const matchesDateRange =
        (!startDate || reqDate >= startDate) &&
        (!endDate || reqDate <= new Date(endDate.setHours(23, 59, 59, 999)));
      return matchesStatus && matchesDateRange;
    });
    setFilteredRequests(filtered);
  }, [searchStatus, searchStartDate, searchEndDate, returnRequests]);

  const toggleDetails = (requestId) => {
    console.log('Toggling details for request:', requestId);
    setExpandedRequest(expandedRequest === requestId ? null : requestId);
  };

  const exportToCSV = () => {
    if (filteredRequests.length === 0) {
      toast.warn("No return requests to export.");
      return;
    }

    let csvContent = "data:text/csv;charset=utf-8,";
    const headers = [
      "Request ID",
      "Worker",
      "Date",
      "Type",
      "Amount",
      "Particulars",
      "Site",
      "Main Head",
      "Sub Head",
      "Purpose",
      "Status",
      "Rejection Reason",
      "Created By",
      "Created At",
      "Updated By",
      "Updated At",
      "Return Type",
      "Transaction Count",
      "Transaction Nos",
      "Transaction Created By",
      "Transaction Created At"
    ];
    csvContent += headers.join(",") + "\r\n";

    filteredRequests.forEach((req) => {
      const row = [
        req.id,
        req.requester_name || req.worker_name || "-",
        req.transactions[0]?.transaction_date ? formatDate(req.transactions[0].transaction_date) : "-",
        req.transactions[0]?.amount_type || "-",
        formatCurrency(req.total_return_amount),
        req.transaction_count > 1 ? `Multiple (${req.transaction_count})` : req.transactions[0]?.particuler_to || "-",
        req.transactions[0]?.site || "-",
        req.transactions[0]?.main_head || "-",
        req.transactions[0]?.sub_head || "-",
        req.purpose || "-",
        req.status || "-",
        req.rejection_reason || "-",
        req.created_by_name || "-",
        formatDateTime(req.created_at),
        req.updated_by_name || "-",
        req.updated_at ? formatDateTime(req.updated_at) : "-",
        req.return_type || "-",
        req.transaction_count || 0,
        req.transactions.map(t => t.transaction_no).join("; "),
        req.transactions.map(t => t.transaction_created_by_name || req.created_by_name || "-").join("; "),
        req.transactions.map(t => formatDateTime(t.transaction_created_date || req.created_at)).join("; ")
      ];
      csvContent += row.join(",") + "\r\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `ReturnRequestReport_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatCurrency = (value) => {
    return value != null ? `₹${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "₹0.00";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).replace(/,/, "");
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 to-blue-100 pt-16 font-sans">
      <Navbar />
      <div className="flex flex-1 relative">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`flex-1 min-h-screen p-2 sm:p-3 overflow-y-auto ${
            isSidebarOpen ? "sm:ml-64" : "ml-0"
          } transition-margin duration-200`}
        >
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h1 className="text-2xl sm:text-4xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-500 mx-auto py-4">
                {reportTitle}
              </h1>
              <div className="w-24"></div>
            </div>

            <div className="mt-6">
              <form
                onSubmit={(e) => e.preventDefault()}
                className="flex flex-col sm:flex-row gap-2 mb-6 max-w-4xl mx-auto"
              >
                <select
                  value={searchStatus}
                  onChange={(e) => setSearchStatus(e.target.value)}
                  className="flex-grow p-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-[10px] sm:text-sm bg-white/90"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <input
                  type="date"
                  value={searchStartDate}
                  onChange={(e) => setSearchStartDate(e.target.value)}
                  placeholder="Start Date (YYYY-MM-DD)"
                  className="flex-grow p-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-[10px] sm:text-sm bg-white/90"
                />
                <input
                  type="date"
                  value={searchEndDate}
                  onChange={(e) => setSearchEndDate(e.target.value)}
                  placeholder="End Date (YYYY-MM-DD)"
                  className="flex-grow p-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-[10px] sm:text-sm bg-white/90"
                />
                <button
                  type="button"
                  onClick={exportToCSV}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center justify-center text-[10px] sm:text-sm transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Export to CSV
                </button>
              </form>

              {isLoading ? (
                <div className="text-center py-8 text-blue-600 text-[10px] sm:text-sm">
                  Loading return requests...
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-600 text-[10px] sm:text-sm">
                  Error: {error}
                </div>
              ) : filteredRequests.length === 0 ? (
                <div className="text-center py-8 text-blue-600 text-[10px] sm:text-sm">
                  No return requests found
                </div>
              ) : (
                <div className="relative overflow-x-auto w-full">
                  <table className="w-full text-[10px] sm:text-xs text-left text-gray-700 shadow-md rounded-lg overflow-hidden">
                    <thead className="text-[10px] sm:text-xs uppercase bg-blue-200 text-black sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-1.5 sticky left-0 bg-blue-200 z-20">Request ID</th>
                        <th className="px-2 py-1.5">{requesterLabel}</th>
                        <th className="px-2 py-1.5">Date</th>
                        <th className="px-2 py-1.5">Type</th>
                        <th className="px-2 py-1.5">Amount</th>
                        <th className="px-2 py-1.5">Particulars</th>
                        <th className="px-2 py-1.5">Site</th>
                        <th className="px-2 py-1.5">Main Head</th>
                        <th className="px-2 py-1.5">Sub Head</th>
                        <th className="px-2 py-1.5">Purpose</th>
                        <th className="px-2 py-1.5">Proof</th>
                        <th className="px-2 py-1.5">Status</th>
                        <th className="px-2 py-1.5">Rejection Reason</th>
                        <th className="px-2 py-1.5">Created By</th>
                        <th className="px-2 py-1.5">Created At</th>
                        <th className="px-2 py-1.5">Updated By</th>
                        <th className="px-2 py-1.5">Updated At</th>
                        <th className="px-2 py-1.5">Return Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((req) => (
                        <React.Fragment key={req.id}>
                          <tr
                            className={`bg-white border-b hover:bg-blue-50 transition-colors duration-200 ${
                              req.status === "pending"
                                ? "bg-yellow-50"
                                : req.status === "approved"
                                ? "bg-green-50"
                                : "bg-red-50"
                            }`}
                          >
                            <td className="px-2 py-1.5 sticky left-0 bg-white z-10 font-medium text-blue-600">{req.id}</td>
                            <td className="px-2 py-1.5">{req.requester_name || req.worker_name || "-"}</td>
                            <td className="px-2 py-1.5">{req.transactions[0]?.transaction_date ? formatDate(req.transactions[0].transaction_date) : "-"}</td>
                            <td className="px-2 py-1.5">
                              <span
                                className={`px-1 py-0.5 rounded-full text-[10px] ${
                                  req.transactions[0]?.amount_type === "credit"
                                    ? "bg-green-100 text-green-700"
                                    : req.transactions[0]?.amount_type === "debit"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {req.transactions[0]?.amount_type || "-"}
                              </span>
                            </td>
                            <td className="px-2 py-1.5">{formatCurrency(req.total_return_amount)}</td>
                            <td className="px-2 py-1.5">
                              {req.transaction_count > 1 ? (
                                <button
                                  onClick={() => toggleDetails(req.id)}
                                  className="text-blue-500 hover:underline text-xs"
                                >
                                  View {req.transaction_count} Transactions
                                </button>
                              ) : (
                                req.transactions[0]?.particuler_to || "-"
                              )}
                            </td>
                            <td className="px-2 py-1.5">{req.transactions[0]?.site || "-"}</td>
                            <td className="px-2 py-1.5">{req.transactions[0]?.main_head || "-"}</td>
                            <td className="px-2 py-1.5">{req.transactions[0]?.sub_head || "-"}</td>
                            <td className="px-2 py-1.5">{req.purpose || "-"}</td>
                            <td className="px-2 py-1.5"><TransactionProofImage path={req.attachment_path || req.transactions[0]?.attachment_path} /></td>
                            <td className="px-2 py-1.5">
                              <span
                                className={`px-1 py-0.5 rounded-full text-[10px] ${
                                  req.status === "pending"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : req.status === "approved"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {req.status || "-"}
                              </span>
                            </td>
                            <td className="px-2 py-1.5">{req.rejection_reason || "-"}</td>
                            <td className="px-2 py-1.5">{req.created_by_name || "-"}</td>
                            <td className="px-2 py-1.5">{formatDateTime(req.created_at)}</td>
                            <td className="px-2 py-1.5">{req.updated_by_name || "-"}</td>
                            <td className="px-2 py-1.5">{req.updated_at ? formatDateTime(req.updated_at) : "-"}</td>
                            <td className="px-2 py-1.5">{req.return_type || "-"}</td>
                          </tr>
                          {req.transaction_count > 1 && expandedRequest === req.id && (
                            <tr className="bg-gray-50">
                              <td colSpan={18} className="px-2 py-1.5">
                                <div className="pl-4">
                                  <h4 className="text-xs font-semibold mb-2">Transaction Details</h4>
                                  <table className="w-full text-[10px] sm:text-xs">
                                    <thead>
                                      <tr className="bg-gray-200">
                                        <th className="px-2 py-1">Transaction No</th>
                                        <th className="px-2 py-1">Amount</th>
                                        <th className="px-2 py-1">Date</th>
                                        <th className="px-2 py-1">Particulars</th>
                                        <th className="px-2 py-1">Site</th>
                                        <th className="px-2 py-1">Main Head</th>
                                        <th className="px-2 py-1">Sub Head</th>
                                        <th className="px-2 py-1">Proof</th>
                                        <th className="px-2 py-1">Created At</th>
                                        <th className="px-2 py-1">Created By</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(req.transactions || []).map((txn) => (
                                        <tr key={txn.transaction_no} className="border-b">
                                          <td className="px-2 py-1">{txn.transaction_no || "-"}</td>
                                          <td className="px-2 py-1">{formatCurrency(txn.return_amount)}</td>
                                          <td className="px-2 py-1">{formatDate(txn.transaction_date)}</td>
                                          <td className="px-2 py-1">{txn.particuler_to || "-"}</td>
                                          <td className="px-2 py-1">{txn.site || "-"}</td>
                                          <td className="px-2 py-1">{txn.main_head || "-"}</td>
                                          <td className="px-2 py-1">{txn.sub_head || "-"}</td>
                                          <td className="px-2 py-1"><TransactionProofImage path={txn.attachment_path || req.attachment_path} /></td>
                                          <td className="px-2 py-1">{formatDateTime(txn.transaction_created_date || req.created_at)}</td>
                                          <td className="px-2 py-1">{txn.transaction_created_by_name || req.created_by_name || "-"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  <div className="sm:hidden absolute top-0 right-0 h-full w-3 bg-gradient-to-l from-gray-200/50 to-transparent pointer-events-none"></div>
                </div>
              )}

              {error && (
                <div className="mt-4 px-4 py-2 bg-red-100 text-red-600 rounded text-[10px] sm:text-sm max-w-4xl mx-auto">
                  {error}
                </div>
              )}
            </div>
          </div>
          <div className="fixed bottom-4 right-4">
            <button
              onClick={() => navigate("/Return")}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
            >
              Back to Return Management
            </button>
          </div>
        </motion.main>
        {isSidebarOpen && screenWidth < 640 && (
          <div
            className="fixed inset-0 bg-black/50 z-30"
            onClick={() => document.querySelector('button[aria-label="Close Sidebar"]').click()}
            aria-hidden="true"
          />
        )}
      </div>

      <style jsx>{`
        .overflow-x-auto {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        @media (max-width: 480px) {
          .overflow-x-auto {
            max-width: 100vw;
          }
          table {
            min-width: 900px;
            width: auto;
          }
        }
        main {
          min-width: 0;
          flex: 1 1 auto;
        }
      `}</style>
    </div>
  );
};

export default ReturnRequestReport;
